import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { z } from 'zod'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Input } from '@/components/ui/Input'
import { describeError } from '@/components/ui/error-copy'
import { AuthHero } from '@/features/auth/AuthHero'
import { register as submitRegistration } from '@/features/auth/api'
import { isApiError } from '@/lib/api/problem'

const emailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/u

export function RegisterPage() {
  const { t } = useTranslation()
  const [done, setDone] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const schema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .trim()
          .min(1, { message: t('auth.errors.emailRequired') })
          .regex(emailPattern, { message: t('auth.errors.emailInvalid') }),
        partnerKey: z
          .string()
          .trim()
          .min(1, { message: t('auth.errors.partnerKeyRequired') }),
        password: z.string().min(12, { message: t('auth.errors.passwordShort') }),
      }),
    [t],
  )

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      partnerKey: '',
      password: '',
    },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null)

    try {
      await submitRegistration({
        email: values.email,
        partnerKey: values.partnerKey,
        password: values.password,
      })

      setDone(true)
    } catch (error) {
      if (isApiError(error) && error.isPartnerValidationFailed) {
        form.setError(
          'partnerKey',
          { message: t('auth.errors.partnerKeyRejected') },
          { shouldFocus: true },
        )
        return
      }

      setFormError(t(describeError(error).description))
    }
  })

  const signInLink = { to: '/login', label: t('auth.register.signIn') }

  if (done) {
    return (
      <AuthHero title={t('auth.register.title')} headerLink={signInLink}>
        <EmptyState
          icon="check"
          title={t('auth.register.doneTitle')}
          description={t('auth.register.doneDescription')}
          action={
            <Link to="/login" className="font-medium text-accent-text underline">
              {t('auth.register.signIn')}
            </Link>
          }
        />
      </AuthHero>
    )
  }

  return (
    <AuthHero
      title={t('auth.register.title')}
      subtitle={t('auth.register.subtitle')}
      headerLink={signInLink}
      footer={
        <>
          {`${t('auth.register.haveAccount')} `}
          <Link to="/login" className="font-medium text-accent-text underline">
            {t('auth.register.signIn')}
          </Link>
        </>
      }
    >
      <form noValidate onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4">
        <Input
          label={t('auth.signIn.email')}
          type="email"
          autoComplete="email"
          required
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />

        <Input
          label={t('auth.register.partnerKey')}
          type="password"
          autoComplete="off"
          spellCheck={false}
          required
          hint={t('auth.register.partnerKeyHint')}
          error={form.formState.errors.partnerKey?.message}
          {...form.register('partnerKey')}
        />

        <Input
          label={t('auth.signIn.password')}
          type="password"
          autoComplete="new-password"
          required
          hint={t('auth.register.passwordHint')}
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />

        {formError === null ? null : (
          <p role="alert" className="text-sm text-error-strong">
            {formError}
          </p>
        )}

        <Button type="submit" loading={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? t('auth.register.submitting') : t('auth.register.submit')}
        </Button>
      </form>
    </AuthHero>
  )
}
